import {apiFetch} from './WorkSheetContext';

/**
 * Lead contact logging — API client + pure helpers.
 * Backend: `leadContact.controller.js` (GET vocabulary, POST/GET/DELETE contacts, ai-next-action)
 * and the `contactAttempt` field of PUT /crm/leads/{id}/status. Raw HTTP (routes newer than the prod spec).
 */

/** Local fallback mirroring HappyPOS-backend `constants/leadContact.js` (used when the vocabulary call fails). */
export const FALLBACK_VOCABULARY = Object.freeze({
    channels: ['phone', 'email', 'personal', 'video', 'sms'],
    outcomes: ['interested', 'callback', 'write-later', 'no-answer', 'left-message', 'send-info', 'meeting-booked',
        'quote-requested', 'not-now', 'not-interested', 'wrong-contact'],
    outcomeSuggestedStatus: {
        'not-interested': 'not-interested', 'quote-requested': 'quote', 'meeting-booked': 'demo-scheduled', interested: 'interested',
    },
    nonConnectOutcomes: ['no-answer', 'left-message', 'wrong-contact'],
    positiveOutcomes: ['interested', 'meeting-booked', 'quote-requested', 'send-info'],
    followUpTypes: ['call', 'email', 'send-quote', 'meeting', 'sms', 'other'],
    outcomeDefaultFollowUpType: {
        callback: 'call', 'write-later': 'email', 'send-info': 'email', 'quote-requested': 'send-quote', 'meeting-booked': 'meeting',
    },
});

/** Outcomes that must carry a follow-up (backend answers 400 otherwise). */
export const FOLLOW_UP_REQUIRED_OUTCOMES = Object.freeze(['callback', 'write-later']);

const idOf = (lead) => lead?._id || lead?.id;

/**
 * Merge a server vocabulary over the local fallback so missing keys never crash the sheet.
 * @param {object} [remote] server vocabulary.
 * @returns {object} complete vocabulary.
 */
export const normalizeVocabulary = (remote) => {
    const src = remote && typeof remote === 'object' ? remote : {};
    const out = {...FALLBACK_VOCABULARY};

    Object.keys(FALLBACK_VOCABULARY).forEach((key) => {
        const val = src[key];
        const fb = FALLBACK_VOCABULARY[key];

        if (Array.isArray(fb) ? Array.isArray(val) && val.length : val && typeof val === 'object' && !Array.isArray(val)) {
            out[key] = val;
        }
    });

    return out;
};

let vocabularyPromise = null;

/**
 * Load the contact vocabulary (cached per session); falls back to the local copy on failure.
 * @returns {Promise<object>} vocabulary.
 */
export const fetchContactVocabulary = () => {
    if (!vocabularyPromise) {
        vocabularyPromise = apiFetch('/crm/lead-contacts/vocabulary')
            .then(normalizeVocabulary)
            .catch(() => {
                vocabularyPromise = null;

                return normalizeVocabulary(null);
            });
    }

    return vocabularyPromise;
};

/**
 * Group outcomes into positive / neutral / negative buckets for the chip rows.
 * @param {object} vocab vocabulary.
 * @returns {{positive: string[], neutral: string[], negative: string[]}} grouped outcome keys (order preserved).
 */
export const groupOutcomes = (vocab) => {
    const v = normalizeVocabulary(vocab);
    const negative = new Set([...(v.nonConnectOutcomes || []), 'not-interested']);
    const positive = new Set(v.positiveOutcomes || []);
    const groups = {positive: [], neutral: [], negative: []};

    v.outcomes.forEach((o) => {
        if (negative.has(o)) {
            groups.negative.push(o);
        } else if (positive.has(o)) {
            groups.positive.push(o);
        } else {
            groups.neutral.push(o);
        }
    });

    return groups;
};

/**
 * "When did it happen" presets: now / 15 minutes ago / 1 hour ago.
 * @param {Date} [now] reference time.
 * @returns {{key: string, date: Date}[]} presets.
 */
export const buildContactTimePresets = (now = new Date()) => [
    {key: 'now', date: new Date(now)},
    {key: 'min15', date: new Date(now.getTime() - 15 * 60000)},
    {key: 'hour1', date: new Date(now.getTime() - 60 * 60000)},
];

const atHour = (base, dayOffset, hour) => {
    const d = new Date(base);

    d.setDate(d.getDate() + dayOffset);
    d.setHours(hour, 0, 0, 0);

    return d;
};

/**
 * Follow-up presets: in 1 hour / today 16:00 / tomorrow 9:00 / tomorrow 14:00 / next Monday 9:00 / in 1 week.
 * Presets that would already be in the past (today 16:00 after 16:00) are omitted.
 * @param {Date} [now] reference time.
 * @returns {{key: string, date: Date}[]} presets, all strictly in the future.
 */
export const buildFollowUpPresets = (now = new Date()) => {
    const day = now.getDay(); // 0 = Sunday
    const toMonday = ((8 - day) % 7) || 7;
    const list = [
        {key: 'hour1', date: new Date(now.getTime() + 60 * 60000)},
        {key: 'today16', date: atHour(now, 0, 16)},
        {key: 'tomorrow9', date: atHour(now, 1, 9)},
        {key: 'tomorrow14', date: atHour(now, 1, 14)},
        {key: 'nextMonday9', date: atHour(now, toMonday, 9)},
        {key: 'week1', date: new Date(now.getTime() + 7 * 24 * 3600000)},
    ];

    return list.filter((p) => p.date.getTime() > now.getTime());
};

/**
 * Is a follow-up mandatory for this outcome?
 * @param {string} outcome outcome key.
 * @returns {boolean} true for callback / write-later.
 */
export const isFollowUpRequired = (outcome) => FOLLOW_UP_REQUIRED_OUTCOMES.includes(outcome);

/**
 * Build the POST body for a contact attempt. Trims texts, drops empty optionals, validates the
 * required follow-up and the future-contact rule locally.
 * @param {object} form sheet state: {channel, outcome, contactedAt, durationMinutes, note, importantNote,
 *   followUpEnabled, followUpType, followUpAt, followUpNote, requestedByClient, applySuggestedStatus}.
 * @param {Date} [now] reference time.
 * @returns {{payload: object|null, error: string|null}} payload, or an error code
 *   (`channel` | `outcome` | `followUpRequired` | `futureContact` | `followUpPast`).
 */
export const buildContactPayload = (form = {}, now = new Date()) => {
    if (!form.channel) {
        return {payload: null, error: 'channel'};
    }

    if (!form.outcome) {
        return {payload: null, error: 'outcome'};
    }

    const contactedAt = form.contactedAt ? new Date(form.contactedAt) : new Date(now);

    if (contactedAt.getTime() > now.getTime() + 60000) {
        return {payload: null, error: 'futureContact'};
    }

    const followAt = form.followUpAt ? new Date(form.followUpAt) : null;
    const wantsFollowUp = !!followAt && (form.followUpEnabled !== false);

    if (isFollowUpRequired(form.outcome) && !wantsFollowUp) {
        return {payload: null, error: 'followUpRequired'};
    }

    if (wantsFollowUp && followAt.getTime() <= now.getTime()) {
        return {payload: null, error: 'followUpPast'};
    }

    const payload = {channel: form.channel, outcome: form.outcome, contactedAt: contactedAt.toISOString()};
    const duration = Number(form.durationMinutes);

    if (Number.isFinite(duration) && duration > 0) {
        payload.durationMinutes = Math.round(duration);
    }

    const note = String(form.note || '').trim();
    const important = String(form.importantNote || '').trim();

    if (note) {
        payload.note = note;
    }

    if (important) {
        payload.importantNote = important;
    }

    if (wantsFollowUp) {
        payload.followUpAt = followAt.toISOString();
        payload.followUp = {
            type: form.followUpType || FALLBACK_VOCABULARY.outcomeDefaultFollowUpType[form.outcome] || 'call',
            dueAt: followAt.toISOString(),
            requestedByClient: !!form.requestedByClient,
        };

        const fNote = String(form.followUpNote || '').trim();

        if (fNote) {
            payload.followUp.note = fNote;
        }
    }

    if (form.applySuggestedStatus) {
        payload.applySuggestedStatus = true;
    }

    return {payload, error: null};
};

/**
 * Pipeline status suggested by an outcome (null if none).
 * @param {object} vocab vocabulary.
 * @param {string} outcome outcome key.
 * @returns {string|null} status key.
 */
export const suggestedStatusFor = (vocab, outcome) => normalizeVocabulary(vocab).outcomeSuggestedStatus[outcome] || null;

/**
 * Summarise a lead's attempts: count, last contact, next open follow-up, pinned important notes.
 * @param {object[]} attempts attempts (any order).
 * @returns {{count: number, last: object|null, nextFollowUp: Date|null, pinned: object[]}} summary.
 */
export const summarizeAttempts = (attempts) => {
    const list = Array.isArray(attempts) ? attempts.filter(Boolean) : [];
    const byTime = [...list].sort((a, b) => new Date(b.contactedAt || 0) - new Date(a.contactedAt || 0));
    const now = Date.now();
    const upcoming = list
        .map((a) => a.followUpAt || a.followUp?.dueAt)
        .filter(Boolean)
        .map((d) => new Date(d))
        .filter((d) => !Number.isNaN(d.getTime()) && d.getTime() > now)
        .sort((a, b) => a - b);

    return {
        count: list.length,
        last: byTime[0] || null,
        nextFollowUp: upcoming[0] || null,
        pinned: byTime.filter((a) => String(a.importantNote || '').trim()),
    };
};

/** GET the lead's attempts (newest first). @param {object} lead lead. @returns {Promise<object[]>} attempts. */
export const listLeadContacts = async (lead) => {
    const res = await apiFetch(`/crm/leads/${idOf(lead)}/contacts`, {skipCache: true});

    return Array.isArray(res) ? res : (res?.items || res?.attempts || []);
};

/**
 * POST a contact attempt. @param {object} lead lead. @param {object} payload from buildContactPayload.
 * @returns {Promise<{attempt: object, lead: object, followUp: object|null}>} 201 body.
 */
export const createLeadContact = (lead, payload) => apiFetch(`/crm/leads/${idOf(lead)}/contacts`, {
    method: 'POST',
    body: JSON.stringify(payload),
});

/** DELETE an attempt. @param {object} lead lead. @param {string} contactId attempt id. @returns {Promise<object>} `{success}`. */
export const deleteLeadContact = (lead, contactId) => apiFetch(`/crm/leads/${idOf(lead)}/contacts/${contactId}`, {method: 'DELETE'});

/**
 * AI next best action. 402/403 = no AI credits (error carries status).
 * @param {object} lead lead. @param {string} locale UI language.
 * @returns {Promise<{action: string, channel: string, suggestedAt: string, reason: string, script: string}>} suggestion.
 */
export const fetchNextAction = (lead, locale) => apiFetch(`/crm/leads/${idOf(lead)}/contacts/ai-next-action`, {
    method: 'POST',
    body: JSON.stringify({locale}),
});

/**
 * Status change + contact in a single request (PUT /crm/leads/{id}/status).
 * @param {object} lead lead. @param {string} status target status.
 * @param {object} payload from buildContactPayload (applySuggestedStatus is lifted to the top level).
 * @returns {Promise<object>} updated lead.
 */
export const statusChangeWithContact = (lead, status, payload) => {
    const {applySuggestedStatus, ...contactAttempt} = payload || {};
    const body = {status, contactAttempt};

    if (applySuggestedStatus) {
        body.applySuggestedStatus = true;
    }

    return apiFetch(`/crm/leads/${idOf(lead)}/status`, {method: 'PUT', body: JSON.stringify(body)});
};
